import { Spin } from 'antd';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './style.css';

const withInfiniteScroll = (
  WrappedComponent,
  {
    initialLoadCount = 10,
    fetchOnScroll = true,
    maxHeight = '100%',
    apiConfig = null,
    mode = 'local', // 'local', 'api', or 'hybrid'
  }
) => {
  return (props) => {
    const { data: allData, type } = props; // `data` is passed as a prop, type is optional
    const [visibleData, setVisibleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const lastFetchedPageRef = useRef(-1); // Use ref instead of state to avoid re-renders
    const fetchInProgressRef = useRef(false); // Track if a fetch is currently in progress

    const dispatch = useDispatch();

    // Get the workspace data from Redux based on type
    const { workspaceList, newWorkspaceList } = useSelector((state) => state.organization);
    const reduxData = (type ? newWorkspaceList?.[type] : workspaceList) || [];

    // Get total count from Redux state
    const fetchWorkspaceByTypeData = useSelector((state) => state.organization?.fetchWorkspaceByTypeData);
    const totalCountFromRedux = type ? fetchWorkspaceByTypeData?.[type]?.total_count ?? 0 : workspaceList?.length;

    const isWorkspaceByTypeLoading = useSelector((state) => state.organization?.loading?.fetchWorkspaceByType);

    // Update totalCount when it changes in Redux
    useEffect(() => {
      if (totalCountFromRedux > 0) {
        setTotalCount(totalCountFromRedux);
        setHasMore(reduxData.length < totalCountFromRedux);
      }
    }, [totalCountFromRedux, reduxData.length]);

    // Initialize data when reduxData changes
    useEffect(() => {
      if (reduxData && reduxData.length > 0) {
        initializeData();
      }
    }, [reduxData]);

    // Initialize data based on the mode
    const initializeData = useCallback(() => {
      if (mode === 'local' && Array.isArray(allData)) {
        // Local data mode
        setVisibleData(allData?.slice(0, initialLoadCount) || []);
        setHasMore(allData?.length > initialLoadCount);
        setInitialLoadDone(true);
      } else if (mode === 'api' || mode === 'hybrid') {
        // For API or Hybrid mode
        if (mode === 'hybrid' && reduxData?.length > 0) {
          // For hybrid mode, first check if we have data in Redux
          const initialChunk = reduxData.slice(0, Math.min(initialLoadCount, reduxData.length));
          setVisibleData(initialChunk);

          // If we now have at least the visible data populated, mark initial load as done
          setInitialLoadDone(true);

          // Update page to reflect the current state based on reduxData length
          const currentPage = Math.ceil(reduxData.length / initialLoadCount);
          if (currentPage > page) {
            setPage(currentPage);
          }
        }
      }
    }, [allData, mode, reduxData, initialLoadCount, page]);

    // Function to fetch data from API
    const fetchDataFromApi = useCallback(
      async (pageNumber) => {
        // Prevent duplicate API calls
        if (
          loading ||
          !hasMore ||
          !apiConfig ||
          pageNumber === lastFetchedPageRef.current ||
          fetchInProgressRef.current
        ) {
          return;
        }

        // Mark fetch as in progress
        fetchInProgressRef.current = true;
        setLoading(true);
        lastFetchedPageRef.current = pageNumber;

        try {
          const { getData, userOrganizationId, type } = apiConfig;

          // Calculate skip based on page number
          const skip = pageNumber * initialLoadCount;

          // Dispatch the action to fetch data
          const result = await dispatch(
            getData({
              type,
              skip,
              limit: initialLoadCount,
              organizationId: userOrganizationId,
            })
          );

          // Process the result
          // (totalCount will be updated via the useEffect that watches totalCountFromRedux)

          // Move to the next page for future loads
          setPage(pageNumber + 1);
          setInitialLoadDone(true);
        } catch (error) {
          console.error('Error fetching data:', error);
          setHasMore(false);
        } finally {
          setLoading(false);
          fetchInProgressRef.current = false;
        }
      },
      [loading, hasMore, apiConfig, dispatch, initialLoadCount]
    );

    // Load more data on scroll
    const loadMoreData = useCallback(() => {
      if (loading || !hasMore || isWorkspaceByTypeLoading || fetchInProgressRef.current) {
        return;
      }
      setLoading(true);
      if (mode === 'local' && Array.isArray(allData)) {
        // Local data mode

        const nextChunkSize = (page + 1) * initialLoadCount;
        const nextVisibleData = allData.slice(0, nextChunkSize);

        setTimeout(() => {
          setVisibleData(nextVisibleData);
          setHasMore(nextVisibleData.length < allData.length);
          setPage(page + 1);
          setLoading(false);
        }, 300);
      } else if (mode === 'api' || mode === 'hybrid') {
        // API or Hybrid mode
        if (mode === 'hybrid') {
          // For hybrid mode, check if we already have enough data in Redux
          const currentDataCount = reduxData?.length || 0;
          const nextChunkSize = page * initialLoadCount;

          // If we have a total count from the API, use it to determine if there's more data
          if (totalCount > 0) {
            setHasMore(currentDataCount < totalCount);

            if (currentDataCount < totalCount && currentDataCount <= nextChunkSize) {
              // Need to fetch more data
              fetchDataFromApi(page);
            } else if (currentDataCount > 0) {
              // We already have enough data in Redux to show the next chunk
              setVisibleData(reduxData.slice(0, Math.min(nextChunkSize, reduxData.length)));
            }
          } else if (currentDataCount > nextChunkSize) {
            // We already have enough data in Redux to show the next chunk
            setVisibleData(reduxData.slice(0, nextChunkSize));
            setPage(page + 1);
          } else {
            // Need to fetch more data from API
            fetchDataFromApi(page);
          }
        } else {
          // Pure API mode
          fetchDataFromApi(page);
        }
      }
    }, [
      loading,
      hasMore,
      mode,
      allData,
      page,
      initialLoadCount,
      reduxData,
      fetchDataFromApi,
      totalCount,
      isWorkspaceByTypeLoading,
    ]);

    // Update visible data when reduxData changes
    useEffect(() => {
      if (reduxData && reduxData.length > 0 && initialLoadDone && type) {
        const visibleCount = Math.min(page * initialLoadCount, reduxData.length);
        setVisibleData(reduxData.slice(0, visibleCount));
      }
    }, [reduxData, page, initialLoadCount, initialLoadDone, type]);

    // Handle scroll event
    const handleScroll = (e) => {
      if (
        !fetchOnScroll ||
        !initialLoadDone ||
        fetchInProgressRef.current ||
        (mode !== 'local' && totalCountFromRedux <= reduxData?.length)
      )
        return;

      const { scrollTop, clientHeight, scrollHeight } = e.target;
      // Load more when scrolling to 80% of the bottom
      if (scrollHeight - scrollTop <= clientHeight * 1.2 && hasMore && !loading && !isWorkspaceByTypeLoading) {
        loadMoreData();
      }
    };

    // For empty initial data but with type, try to fetch on mount
    useEffect(() => {
      if (
        type &&
        mode !== 'local' &&
        apiConfig &&
        reduxData.length === 0 &&
        !loading &&
        !isWorkspaceByTypeLoading &&
        !initialLoadDone &&
        !fetchInProgressRef.current
      ) {
        fetchDataFromApi(0);
      }
    }, [type, mode, apiConfig, reduxData, loading, initialLoadDone, isWorkspaceByTypeLoading, fetchDataFromApi]);

    return (
      <div
        onScroll={handleScroll}
        style={{ overflowY: 'auto', maxHeight: maxHeight }}
        className="infinite-scroll-container"
      >
        <WrappedComponent {...props} data={visibleData} loading={loading || isWorkspaceByTypeLoading} />
        {(loading || isWorkspaceByTypeLoading) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
            <Spin />
          </div>
        )}
      </div>
    );
  };
};

export default withInfiniteScroll;
